import { ColDef } from 'ag-grid-community';
import { IntlShape } from 'react-intl';
import { RIGHT_ALIGNED, RIGHT_ALIGNED_STYLES } from 'common/constants';
import { tooltipGenerator } from 'components/ComposedTable/utils';
import { columnHeaders, labels } from 'pages/VendorsTablePage/messages';
import { formatLocaleDate } from 'utils/dateUtils';
import { formatCurrency } from 'utils/formatCurrency';
import { VENDORS_TABLE_FIELDS } from './constants';
import {
  getFirstChildData,
  getNumberFromCurrency,
  sortDollarAmounts
} from './helpers';
import type { ITooltipParams } from 'ag-grid-community';

export const vendorsTableColumns = (intl: IntlShape): ColDef[] => [
  {
    field: VENDORS_TABLE_FIELDS.vendorNo,
    rowGroup: true,
    hide: true,
    suppressColumnsToolPanel: true
  },
  {
    field: VENDORS_TABLE_FIELDS.vendorName,
    headerName: intl.formatMessage(columnHeaders.vendorName),
    width: 250,
    aggFunc: 'first',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tooltipValueGetter: (params: ITooltipParams): any =>
      tooltipGenerator(params)
  },
  {
    field: VENDORS_TABLE_FIELDS.paymentMethod,
    headerName: intl.formatMessage(columnHeaders.paymentMethod),
    aggFunc: 'first'
  },
  {
    field: VENDORS_TABLE_FIELDS.invoiceId,
    headerName: intl.formatMessage(columnHeaders.invoiceId)
  },
  {
    field: VENDORS_TABLE_FIELDS.amount,
    headerName: intl.formatMessage(columnHeaders.amount),
    type: RIGHT_ALIGNED,
    cellStyle: RIGHT_ALIGNED_STYLES,
    aggFunc: params => {
      const data = getFirstChildData(params);
      return formatCurrency(
        getNumberFromCurrency(data?.totalAmount),
        'decimal'
      );
    },
    comparator: (firstAmount: string, secondAmount: string) =>
      sortDollarAmounts(firstAmount, secondAmount),
    valueFormatter: ({ value }) =>
      formatCurrency(getNumberFromCurrency(value), 'decimal')
  },
  {
    field: VENDORS_TABLE_FIELDS.discountAmount,
    headerName: intl.formatMessage(columnHeaders.discountAmount),
    width: 180,
    type: RIGHT_ALIGNED,
    cellStyle: RIGHT_ALIGNED_STYLES,
    aggFunc: params => {
      const data = getFirstChildData(params);
      return formatCurrency(
        getNumberFromCurrency(data?.totalDiscountAmount),
        'decimal'
      );
    },
    comparator: (firstAmount: string, secondAmount: string) =>
      sortDollarAmounts(firstAmount, secondAmount),
    valueFormatter: ({ value }) =>
      formatCurrency(getNumberFromCurrency(value), 'decimal')
  },
  {
    field: VENDORS_TABLE_FIELDS.netAmount,
    headerName: intl.formatMessage(columnHeaders.netPayment),
    type: RIGHT_ALIGNED,
    cellStyle: RIGHT_ALIGNED_STYLES,
    aggFunc: params => {
      const data = getFirstChildData(params);
      return formatCurrency(
        getNumberFromCurrency(data?.totalNetAmount),
        'decimal'
      );
    },
    comparator: (firstAmount: string, secondAmount: string) =>
      sortDollarAmounts(firstAmount, secondAmount),
    valueFormatter: ({ value }) =>
      formatCurrency(getNumberFromCurrency(value), 'decimal')
  },
  {
    field: VENDORS_TABLE_FIELDS.invoiceDate,
    headerName: intl.formatMessage(columnHeaders.invoiceDate),
    cellDataType: 'text',
    valueFormatter: ({ value }) => formatLocaleDate(value, intl.locale)
  },
  {
    field: VENDORS_TABLE_FIELDS.dueDate,
    headerName: intl.formatMessage(columnHeaders.dueDate),
    cellDataType: 'text',
    valueFormatter: ({ value }) => formatLocaleDate(value, intl.locale)
  },
  {
    field: VENDORS_TABLE_FIELDS.onHold,
    headerName: intl.formatMessage(columnHeaders.hold),
    aggFunc: params => {
      const data = getFirstChildData(params);
      return data?.vendorOnHold;
    },
    cellDataType: 'text',
    valueFormatter: ({ value }) => (value ? intl.formatMessage(labels.yes) : '')
  }
];
